// components/CaseTagReplaceDialog.jsx
// Delete a case tag that has dependent Customer Specs: pick a replacement tag (or "No case
// tag"), which reassigns every dependent spec, then deletes — one transactional backend call
// (POST /api/casetags/:id/replace-delete). Replaces the Block-and-Inform dialog for case tags.
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AlertTriangle, X } from 'lucide-react';
import TypeaheadSelect from './TypeaheadSelect';
import { caseTagScopeLabels } from '../utils/caseTagScope';

const NO_TAG_VALUE = '__NONE__';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: var(--shadow-a35);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Dialog = styled.div`
  background: var(--white);
  border-radius: 16px;
  box-shadow: 0 24px 64px var(--shadow-a25);
  width: min(560px, 100%);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  padding: 22px 22px 18px;
  position: relative;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 30px;
  height: 30px;
  background: transparent;
  border: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ink-a60);
  cursor: pointer;

  &:hover { background: var(--shadow-a05); color: var(--black); }
  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: 2px; }
`;

const Title = styled.h2`
  margin: 0 0 6px;
  font-size: 1.05rem;
  color: var(--color-danger);
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
`;

const Body = styled.p`
  margin: 0 0 14px;
  font-size: 0.92rem;
  color: var(--black);
`;

const DepBlock = styled.div`
  border: 1px solid var(--shadow-a08);
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 14px;
`;

const DepHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-weight: 700;
  color: var(--black);
`;

const Badge = styled.span`
  background: var(--danger-a10);
  color: var(--color-danger);
  font-size: 0.78rem;
  font-weight: 800;
  border-radius: 999px;
  padding: 2px 10px;
  min-width: 26px;
  text-align: center;
`;

const DepList = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: var(--ink-a60);
  font-size: 0.85rem;

  li + li { margin-top: 2px; }
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--ink-a70);
  margin: 4px 0 6px;
`;

const Foot = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;

const BtnSecondary = styled.button`
  height: 34px;
  padding: 0 14px;
  border: 1px solid var(--shadow-a08);
  border-radius: 8px;
  background: var(--white);
  color: var(--black);
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;

  &:hover { background: var(--grey-100); }
  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: 2px; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const BtnDanger = styled.button`
  height: 34px;
  padding: 0 14px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: var(--color-danger);
  color: var(--white);
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;

  &:hover { background: var(--color-danger-hover); }
  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: 2px; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

export default function CaseTagReplaceDialog({
  open,
  tag,                // the case tag being deleted (row from /api/casetags)
  dependents,         // { specs: { count, sample:[{id,name}], hasMore } }
  caseTags = [],      // full tag list, to build the "Replace with" options
  customers = [],     // /api/customers/dropdown rows, to resolve CustomersCsv ids to names
  busy = false,
  onCancel,
  onConfirm,          // (replaceWith: number | null) => void
}) {
  const [replaceWith, setReplaceWith] = useState('');

  // Reset the selection whenever the dialog (re)opens for a tag.
  useEffect(() => { if (open) setReplaceWith(''); }, [open, tag]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onCancel && onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  const customerNameById = useMemo(() => {
    const m = new Map();
    for (const c of customers || []) m.set(String(c.UniqueID), String(c.Customer || ''));
    return m;
  }, [customers]);

  const options = useMemo(() => {
    const others = (caseTags || []).filter((ct) => String(ct.UniqueID) !== String(tag?.UniqueID));
    const tagOpts = others.map((ct) => {
      const scope = caseTagScopeLabels(ct, customerNameById).join(', ') || 'No customer';
      return {
        value: String(ct.UniqueID),
        label: `${ct.CaseTagName || `Tag #${ct.UniqueID}`} — ${scope}`,
      };
    });
    return [{ value: NO_TAG_VALUE, label: '(No case tag)' }, ...tagOpts];
  }, [caseTags, tag, customerNameById]);

  if (!open || !tag) return null;

  const count = dependents?.specs?.count || 0;
  const sample = dependents?.specs?.sample || [];
  const hasMore = dependents?.specs?.hasMore;
  const canConfirm = replaceWith !== '' && !busy;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm && onConfirm(replaceWith === NO_TAG_VALUE ? null : Number(replaceWith));
  };

  return (
    <Backdrop onClick={onCancel} role="presentation">
      <Dialog onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="casetag-replace-title">
        <CloseBtn type="button" onClick={onCancel} aria-label="Close dialog">
          <X size={16} aria-hidden="true" />
        </CloseBtn>

        <Title id="casetag-replace-title">
          <AlertTriangle size={18} aria-hidden="true" />
          Delete case tag &ldquo;{tag.CaseTagName}&rdquo;
        </Title>
        <Body>
          {count} customer specification{count === 1 ? '' : 's'} use this case tag. Pick a
          replacement (or &ldquo;No case tag&rdquo;) to reassign {count === 1 ? 'it' : 'them'} to,
          then delete.
        </Body>

        <DepBlock>
          <DepHead>
            <Badge>{count}</Badge>
            Customer Specification{count === 1 ? '' : 's'}
          </DepHead>
          {sample.length > 0 && (
            <DepList>
              {sample.map((s) => (
                <li key={`s-${s.id}`}>{s.name || `Spec #${s.id}`}</li>
              ))}
              {hasMore && <li>…and more</li>}
            </DepList>
          )}
        </DepBlock>

        <FieldLabel htmlFor="casetag-replace-with">Replace with</FieldLabel>
        <TypeaheadSelect
          ariaLabel="Replace with"
          value={replaceWith}
          placeholder="Select replacement…"
          size="lg"
          options={options}
          onChange={(v) => setReplaceWith(String(v || ''))}
        />

        <Foot>
          <BtnSecondary type="button" onClick={onCancel} disabled={busy}>Cancel</BtnSecondary>
          <BtnDanger type="button" onClick={handleConfirm} disabled={!canConfirm}>
            Reassign all {count} &amp; Delete
          </BtnDanger>
        </Foot>
      </Dialog>
    </Backdrop>
  );
}
